import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { GLASS } from './glass';

// Same numbers as the pre-order tier on /business — single source of truth there.
const PRICE = 35;
const SEGMENTS = [
    { key: 'ingredients', value: 11.50, color: '#0047AB', label_en: 'Ingredients & pouch', label_nl: 'Ingrediënten & zakje' },
    { key: 'manufacturing', value: 2.00, color: '#1E6FCC', label_en: 'Production & QC', label_nl: 'Productie & QC' },
    { key: 'fulfilment', value: 4.00, color: '#4B93DB', label_en: 'Fulfilment & shipping', label_nl: 'Fulfilment & verzending' },
    { key: 'payment', value: 1.15, color: '#7FB0E5', label_en: 'Payment processing', label_nl: 'Betaalverwerking' },
    { key: 'acquisition', value: 9.00, color: '#FBCFE8', label_en: 'Customer acquisition', label_nl: 'Klantacquisitie' },
    { key: 'operations', value: 2.50, color: '#CBD5E1', label_en: 'Operations', label_nl: 'Operations' },
    { key: 'vat', value: 2.89, color: '#FDE68A', label_en: 'VAT (9%)', label_nl: 'BTW (9%)' },
    { key: 'margin', value: 1.96, color: '#BBF7D0', label_en: 'Net margin', label_nl: 'Nettomarge' },
];

const COPY = {
    en: {
        eyebrow: 'Price transparency',
        heading_1: 'Full transparency.',
        heading_2: 'Right down to the price.',
        body: 'Every pre-order costs €35. Here’s exactly where that money goes, using the same figures we publish on our transparency page.',
        cta: 'See the full breakdown',
        you_pay: 'You pay',
        market_pct: '~33%',
        market_note: 'of the price goes on the ingredients themselves. Across the wider supplement market, that share is often closer to 10–15%.',
    },
    nl: {
        eyebrow: 'Prijstransparantie',
        heading_1: 'Volledige transparantie.',
        heading_2: 'Ook over de prijs.',
        body: 'Elke pre-order is €35. Zo is dat bedrag daadwerkelijk opgebouwd, met dezelfde cijfers die we op de transparantiepagina publiceren.',
        cta: 'Bekijk de volledige opbouw',
        you_pay: 'Je betaalt',
        market_pct: '~33%',
        market_note: 'van de prijs gaat naar de ingrediënten zelf. In de bredere supplementenmarkt ligt dat aandeel vaak dichter bij 10–15%.',
    },
};

export function PriceTransparencySection() {
    const { lang } = useLanguage();
    const c = COPY[lang] || COPY.en;

    return (
        <section className="section-padding relative" id="price-transparency" style={{ scrollMarginTop: '120px' }} data-testid="price-transparency-section">
            <div className="section-container">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`relative mt-10 max-w-2xl mb-6 md:mb-8 p-6 sm:p-8 lg:p-10 ${GLASS}`}
                >
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-primary absolute -top-8 left-1">
                        <span className="w-8 h-px bg-primary" />
                        {c.eyebrow}
                    </div>
                    <h2 className="heading-lg text-foreground mb-4 leading-tight">
                        {c.heading_1}
                        <br />
                        <span className="text-secondary-dark">{c.heading_2}</span>
                    </h2>
                    <p className="text-muted-foreground text-base leading-relaxed">{c.body}</p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`p-5 md:p-7 ${GLASS}`}
                >
                    {/* Header row: JE BETAALT €35 + CTA */}
                    <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
                        <div className="flex items-baseline gap-2.5">
                            <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                                {c.you_pay}
                            </span>
                            <span className="font-condensed font-extrabold text-3xl md:text-4xl text-foreground leading-none tracking-tight" data-testid="price-transparency-amount">
                                €35
                            </span>
                        </div>
                        <Link
                            to="/business"
                            className="hidden sm:inline-flex items-center gap-2 text-primary font-semibold text-sm hover:underline"
                            data-testid="price-breakdown-link"
                        >
                            {c.cta}
                            <ArrowRight size={14} />
                        </Link>
                    </div>

                    {/* Animated stacked bar */}
                    <div className="flex rounded-md overflow-hidden h-4 md:h-5 bg-slate-100 mb-6" data-testid="price-bar" role="img" aria-label={lang === 'nl' ? 'Opbouw van de prijs van €35' : 'Breakdown of the €35 price'}>
                        {SEGMENTS.map((seg, i) => (
                            <motion.div
                                key={seg.key}
                                initial={{ width: 0 }}
                                whileInView={{ width: `${(seg.value / PRICE) * 100}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.1 + i * 0.06, ease: 'easeOut' }}
                                style={{ background: seg.color }}
                                className="h-full"
                                data-testid={`price-seg-${seg.key}`}
                            />
                        ))}
                    </div>

                    {/* Legend — 2 tidy columns on desktop, 1 on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-0">
                        {SEGMENTS.map((seg, i) => (
                            <motion.div
                                key={seg.key}
                                initial={{ opacity: 0, x: -6 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3, delay: 0.15 + i * 0.04 }}
                                className="flex items-center justify-between gap-4 py-2 border-b border-border/40"
                                data-testid={`price-legend-${seg.key}`}
                            >
                                <span className="flex items-center gap-2.5 min-w-0">
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: seg.color }} />
                                    <span className="text-sm text-slate-600 truncate">{lang === 'nl' ? seg.label_nl : seg.label_en}</span>
                                </span>
                                <span className="font-condensed font-semibold text-sm text-slate-800 leading-none whitespace-nowrap tabular-nums">
                                    €{seg.value.toFixed(2)}
                                </span>
                            </motion.div>
                        ))}
                    </div>

                    {/* Subtle market comparison */}
                    <div className="mt-5 pt-4 border-t border-border/60" data-testid="price-market-note">
                        <p className="text-xs md:text-[13px] text-muted-foreground leading-relaxed">
                            <span className="font-condensed font-bold text-foreground">{c.market_pct}</span>{' '}
                            {c.market_note}
                        </p>
                    </div>

                    {/* CTA — mobile only, at the bottom */}
                    <div className="mt-5 sm:hidden">
                        <Link
                            to="/business"
                            className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:underline"
                            data-testid="price-breakdown-link-mobile"
                        >
                            {c.cta}
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
